import {
  addToast,
  Button,
  ColumnTable,
  DisplayInfo,
  Icon,
  Loading,
  Table,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Col, Row } from 'reactstrap';
import { useActionKitImp } from '../../../components/ozmoit/common/action-kit-imp';
import { TranslationsKeys } from '../../../locales/translations';
import { downloadFromBlob } from '../../../utils/blob';
import { exportSummaryAction, importTemplateAction } from '../actions';
import { useIsQueryEnabled } from '../hooks/useIsQueryEnabled';
import { useWarningsSummaryDataLoader } from '../loaders/StudentWarningsSummaryDataLoader';
import { useWarningsSummaryFormLoaderData } from '../loaders/StudentWarningsSummaryFormLoader';
import { StudentWarningsGroupType, StudentWarningsSummaryRow } from '../types';
import { buildDetailsURL } from '../utils';
import MassiveImportModal, {
  MassiveImportModalMethods,
} from './MassiveImportModal';
import RiskLevelBadge from './RiskLevelBadge';
import StudentWarningsSummaryWarningsModal, {
  StudentWarningsSummaryWarningsModalMethods,
} from './StudentWarningsSummaryWarningsModal';

const StudentWarningsSummaryTable = () => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_SUMMARY);
  const detailsRef = useRef<StudentWarningsSummaryWarningsModalMethods>(null);
  const massiveRef = useRef<MassiveImportModalMethods>(null);
  const data = useWarningsSummaryFormLoaderData();
  const {
    loading,
    query,
    result,
    updateQuery,
    refresh,
  } = useWarningsSummaryDataLoader();
  const isQueryEnabled = useIsQueryEnabled(query);
  const history = useHistory();

  const { action: handleExport, loading: exporting } = useActionKitImp(
    exportSummaryAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`export.success`),
        });
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`export.failed`),
          });
        },
      },
    },
  );

  const {
    action: handleDownloadTemplate,
    loading: downloading,
  } = useActionKitImp(importTemplateAction, {
    onError: {
      unknown: () => {
        addToast({
          color: 'danger',
          text: t(`massiveImport.failedTemplate`),
        });
      },
    },
  });

  const handlePressExport = useCallback(
    async (row: StudentWarningsSummaryRow) => {
      if (exporting) {
        return;
      }
      const response = await handleExport(
        {
          studyPlanEnrollmentId: row.studyPlanEnrollmentId.toString(),
          year: query.year?.toString() || '',
          periodId: query.periodId?.toString() || '',
          startAt: query.startAt?.toString() || '',
          endAt: query.endAt?.toString() || '',
        },
        query?.warningGroupType && query?.warningGroupType !== 'summary'
          ? (query?.warningGroupType as StudentWarningsGroupType)
          : undefined,
      );
      if (!response.success) {
        return;
      }

      const blob = new Blob([response.data], {
        type: 'vnd.ms-excel',
      });
      downloadFromBlob(blob, `${t(`export.title`)}.xlsx`);
    },
    [handleExport, query, exporting, t],
  );

  const handlePressDownloadTemplate = useCallback(async () => {
    if (
      downloading ||
      !query?.warningGroupType ||
      query?.warningGroupType === 'summary'
    ) {
      return;
    }
    const response = await handleDownloadTemplate(
      query?.warningGroupType as StudentWarningsGroupType,
    );
    if (!response.success) {
      return;
    }

    const blob = new Blob([response.data], {
      type: 'vnd.ms-excel',
    });
    downloadFromBlob(
      blob,
      `${t(
        `massiveImport.templateName`,
      )} ${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`,
    );
  }, [handleDownloadTemplate, query, downloading, t]);

  const handleUploadTemplate = useCallback(async () => {
    massiveRef?.current?.open();
  }, []);

  const seeDetails = useCallback(
    (row: StudentWarningsSummaryRow) => {
      if (!isQueryEnabled || !data?.warningGroups?.length) {
        return;
      }
      if (query.warningGroupType === 'summary') {
        return history.push(
          buildDetailsURL(row.studyPlanEnrollmentId, {
            warningGroupType: data?.warningGroups[0].value as string,
            periodId: query.periodId ?? '',
            year: query.year ?? '',
            startAt: query.startAt,
            endAt: query.endAt,
          }),
        );
      } else {
        return history.push(
          buildDetailsURL(row.studyPlanEnrollmentId, {
            warningGroupType: query.warningGroupType,
            periodId: query.periodId ?? '',
            year: query.year ?? '',
            startAt: query.startAt,
            endAt: query.endAt,
          }),
        );
      }
    },
    [history, query, isQueryEnabled, data],
  );

  const columns = useMemo<ColumnTable<StudentWarningsSummaryRow>[]>(
    () => [
      {
        columnName: 'name',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.name`),
        cellFormat: ({ row }) => (
          <div className="d-block text-center">
            <span className="d-block">{row.name}</span>
            <strong className="d-block">{row.identification}</strong>
          </div>
        ),
      },
      {
        columnName: 'studyPlan',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.studyPlan`),
      },
      {
        columnName: 'campus',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.campus`),
      },
      {
        columnName: 'promo',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.promo`),
      },
      {
        columnName: 'course',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.course`),
      },
      {
        columnName: 'warnings',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.warnings`),
        cellFormat: ({ row }) => {
          const warningCount = Object.values(row.warnings).reduce(
            (a, b) => a + b,
            0,
          );
          return (
            <div>
              <span>{warningCount}</span>
              {!!(warningCount && query.warningGroupType === 'summary') && (
                <button
                  className="btn btn-sm"
                  type="button"
                  onClick={() => detailsRef?.current?.open(row)}
                >
                  <Icon name="information" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        columnName: 'riskLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText:
          query.warningGroupType === 'summary'
            ? t(`table.headers.riskLevel.summary`)
            : t(`table.headers.riskLevel.other`),
        cellFormat: ({ row }) => <RiskLevelBadge riskLevel={row.riskLevel} />,
      },
      {
        columnName: 'download',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.download`),
        cellFormat: ({ row }) => {
          const warningCount = Object.values(row.warnings).reduce(
            (a, b) => a + b,
            0,
          );
          return (
            <div>
              {!exporting ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => handlePressExport(row)}
                  disabled={!warningCount}
                >
                  <Icon
                    name="download"
                    color={!warningCount ? 'disabled' : 'primary'}
                  />
                </button>
              ) : (
                <Button text="" loading size="sm" />
              )}
            </div>
          );
        },
      },
      {
        columnName: 'action',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.action`),
        cellFormat: ({ row }) => (
          <div>
            <Button
              text={t(`table.actions.manage`)}
              size="sm"
              onClick={() => seeDetails(row)}
            />
          </div>
        ),
      },
    ],
    [t, query.warningGroupType, seeDetails, handlePressExport, exporting],
  );

  if (!isQueryEnabled) {
    return (
      <div className="mt-4 d-block">
        <DisplayInfo
          insideCard
          title={t(`noFiltersSelected.summary.title`)}
          textBody={t(`noFiltersSelected.summary.description`)}
        />
      </div>
    );
  }

  if (!result?.success && loading) {
    return (
      <>
        <Loading textBody={''} />
      </>
    );
  }

  if (!result?.success) {
    return <></>;
  }

  return (
    <>
      <div className="w-100 mt-4 d-block">
        {query?.warningGroupType === StudentWarningsGroupType.Health && (
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <p>{t(`massiveImport.description`)}</p>
            </Col>
            <Col xs={12} md={6}>
              <div className="d-flex justify-content-end">
                <Button
                  text={t(`massiveImport.downloadTemplate`)}
                  size="md"
                  type="button"
                  icon="download"
                  onClick={handlePressDownloadTemplate}
                  loading={downloading}
                  outlined
                />
                <Button
                  text={t(`massiveImport.import`)}
                  className="ml-2"
                  size="md"
                  type="button"
                  icon="upload"
                  disabled={downloading}
                  onClick={handleUploadTemplate}
                />
              </div>
            </Col>
          </Row>
        )}
        <Table
          data={result?.data?.data ?? []}
          columns={columns}
          isLoadingResults={loading}
          pagination={{
            itemsPerPage: query.items_per_page,
            currentPage: query.page,
            totalItems: result.data.total,
            totalPages: result.data.total_pages,
            onChangePage: (nextPage) => {
              updateQuery({
                ...query,
                page: nextPage,
              });
            },
          }}
        />
      </div>
      <StudentWarningsSummaryWarningsModal ref={detailsRef} />
      <MassiveImportModal
        ref={massiveRef}
        schoolId={query.schoolId ? +query.schoolId : 0}
        warningGroupType={query.warningGroupType as StudentWarningsGroupType}
        onUpload={refresh}
      />
    </>
  );
};

export default StudentWarningsSummaryTable;
